import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AreaChart, YAxis, XAxis, CartesianGrid, Tooltip, Area } from 'recharts';
import { AreaDot } from 'recharts/types/cartesian/Area';
import { UtcTime } from 'shared/models';
import { formatBytes } from 'util/formatBytes';
import { formatHourMinuteLabel, formatSizeTickLabel, formatTimeTickLabel } from '../checkInCharts/CheckInCharts.lib';

export type NetworkData = {
    name: number;
    rx: number | null;
    tx: number | null;
};

export type Props = {
    fromDate: UtcTime;
    toDate: UtcTime;
    timeZone: string;
    ticks: number[];
    data: NetworkData[];
};

const activeDotStyle: AreaDot = {
    stroke: '#fff',
    fill: '#8884d8',
    strokeWidth: 1,
};


export const NetworkChart = ({ fromDate, toDate, ticks, data, timeZone }: Props) => {
    const { t, i18n } = useTranslation();

    const toolTipLabels: Record<string, string> = useMemo(() => ({
        tx: t('LABEL.SENT'),
        rx: t('LABEL.RECEIVED'),
    }), [ t ]);

    const toolTipFormatter = useCallback((value: number, dataKey: string) => {
        return [
            formatBytes(t, Number(value)),
            toolTipLabels[dataKey],
        ];
    }, [ toolTipLabels, t ]);

    return (
        <AreaChart width={ 1024 } height={ 180 } data={ data }>
            <defs>
                <linearGradient id="lg1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8884d8" stopOpacity={ .8 }/>
                    <stop offset="95%" stopColor="#8884d8" stopOpacity={ .1 }/>
                </linearGradient>
                <linearGradient id="lg2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0f0" stopOpacity={ .8 }/>
                    <stop offset="95%" stopColor="#0f0" stopOpacity={ .1 }/>
                </linearGradient>
            </defs>
            <CartesianGrid stroke="#ccc" strokeDasharray="3 3" />
            <XAxis type="number" domain={ [ fromDate, toDate ] } dataKey="name" ticks={ ticks } tickFormatter={ formatTimeTickLabel(timeZone, i18n) }/>
            <YAxis tickFormatter={ formatSizeTickLabel(t) } />
            <Tooltip
                labelFormatter={ formatHourMinuteLabel(timeZone, i18n) }
                formatter={ toolTipFormatter }
                separator=": "
            />
            <Area type="monotone" dataKey="tx" stackId="1" fill="url(#lg1)" isAnimationActive={ false } activeDot={ activeDotStyle } />
            <Area type="monotone" dataKey="rx" stackId="2" fill="url(#lg2)" isAnimationActive={ false } activeDot={ activeDotStyle } />
        </AreaChart>
    );
};
